import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { store } from "app/store";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RECORDING,
  RECORDING_STATE_PAUSED,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED
} from "features/medical-scribe/medical-scribe-constants";
import {
  selectMedicalScribeRecordingState,
  selectStreamingScribeId
} from "features/medical-scribe/medical-scribe.slice";
import type { ResumePausedScribeFailureReason } from "features/medical-scribe/streaming/resume-guards.util";
import { useMedicalScribe } from "features/medical-scribe/use-medical-scribe.hook";
import {
  AMBIENT_SCRIBE_REFETCH,
  eventBus
} from "hooks/use-event-bus/event-bus";
import {
  DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED,
  ambientScribeApi,
  useCompleteAmbientScribeMutation,
  useEditAmbientScribeMutation,
  useGetAmbientScribeQuery,
  useGetScribeAudioChunksQuery,
  usePauseAmbientScribeMutation
} from "services/ambient-scribe";
import { getFullName } from "utils/common";

import {
  AWAITING_COMPLETION_POLL_MS,
  AWAITING_COMPLETION_TIMEOUT_MS,
  computeIsResumePleaseWait,
  CONTEXT_SAVE_FLUSH_TIMEOUT_MS,
  shouldClearAwaitingCompletion
} from "./paused-scribe-actions.util";
import { hasTranscribedAudioChunks } from "./recording-sessions-table.util";

const getResumeFailureMessage = (
  reason: ResumePausedScribeFailureReason
): string => {
  switch (reason) {
    case "stop":
    case "pause":
    case "connect":
    case "in_flight":
      return "Finishing a previous recording operation — wait a moment and try again.";
    case "timeout":
      return "Resume is taking longer than expected — wait a moment and try again.";
    case "missing_params":
      return "Cannot resume — missing patient or scribe information.";
    case "start_failed":
    default:
      return "Could not resume recording. Please try again.";
  }
};

type PausedScribePatient = {
  id?: number;
  first_name?: string;
  last_name?: string;
};

type UsePausedScribeActionsParams = {
  scribeId: number | null | undefined;
  scribeStatus?: number;
  patient?: PausedScribePatient | null;
  chartNoteId?: number | null;
  onClose?: () => void;
  onFinalized?: () => void;
  onBeforeAction?: () => void | Promise<void>;
};

export function usePausedScribeActions({
  scribeId,
  scribeStatus,
  patient,
  chartNoteId,
  onClose,
  onFinalized,
  onBeforeAction
}: UsePausedScribeActionsParams) {
  const dispatch = useAppDispatch();
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);
  const widgetScribeId = useAppSelector(selectStreamingScribeId);
  const {
    resumePausedScribe,
    stopRecording,
    prepareForResumeFromEditScribe
  } = useMedicalScribe();

  const [editScribe] = useEditAmbientScribeMutation();
  const [completeScribe, { isLoading: isCompleteLoading }] =
    useCompleteAmbientScribeMutation();
  const [pauseScribe] = usePauseAmbientScribeMutation();

  const [isResuming, setIsResuming] = useState(false);
  const [pendingComplete, setPendingComplete] = useState(false);
  const [awaitingCompletionTransition, setAwaitingCompletionTransition] =
    useState(false);
  const [isTerminalCompleteInFlight, setIsTerminalCompleteInFlight] =
    useState(false);
  const pausedMarkAttemptedRef = useRef(false);
  const deferredOnFinalizedRef = useRef(false);
  const onFinalizedRef = useRef(onFinalized);
  const resumeActionInFlightRef = useRef(false);

  useEffect(() => {
    onFinalizedRef.current = onFinalized;
  }, [onFinalized]);

  const isWidgetActive =
    recordingState !== RECORDING_STATE_STOPPED &&
    widgetScribeId != null &&
    scribeId != null &&
    widgetScribeId === Number(scribeId);

  const isLocallyPausedWidget =
    widgetScribeId != null &&
    scribeId != null &&
    widgetScribeId === Number(scribeId) &&
    recordingState === RECORDING_STATE_PAUSED;

  const isRecordingStatus = scribeStatus === AMBIENT_SCRIBE_STATUS_RECORDING;
  const isProcessingStatus = scribeStatus === AMBIENT_SCRIBE_STATUS_PROCESSING;
  const isScribePaused = scribeStatus === AMBIENT_SCRIBE_STATUS_PAUSED;
  const isResumePleaseWait = computeIsResumePleaseWait({ isResuming });
  const isFailedOrCancelledStatus =
    scribeStatus === AMBIENT_SCRIBE_STATUS_FAILED ||
    scribeStatus === AMBIENT_SCRIBE_STATUS_CANCELLED;

  const {
    data: audioChunks = [],
    isLoading: isAudioChunksLoading,
    isFetching: isAudioChunksFetching,
    isUninitialized: isAudioChunksUninitialized,
    isError: isAudioChunksError
  } = useGetScribeAudioChunksQuery(
    { scribeId: Number(scribeId) },
    { skip: !scribeId || !isFailedOrCancelledStatus }
  );

  const chunksReady =
    !isAudioChunksLoading &&
    !isAudioChunksFetching &&
    !isAudioChunksUninitialized &&
    !isAudioChunksError;

  const canCompleteFromFailedOrCancelled =
    isFailedOrCancelledStatus &&
    chunksReady &&
    hasTranscribedAudioChunks(audioChunks);

  const isFailedOrCancelledAwaitingChunks =
    isFailedOrCancelledStatus && !chunksReady;

  const shouldPollScribe = pendingComplete || awaitingCompletionTransition;

  const { data: freshScribe } = useGetAmbientScribeQuery(
    { id: Number(scribeId), query: DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED },
    {
      skip: !scribeId,
      pollingInterval: shouldPollScribe ? AWAITING_COMPLETION_POLL_MS : 0
    }
  );
  const freshStatus = freshScribe?.status ?? scribeStatus;

  const isCompleting =
    isCompleteLoading ||
    pendingComplete ||
    awaitingCompletionTransition ||
    isTerminalCompleteInFlight;

  const clearPendingComplete = useCallback(() => {
    setPendingComplete(false);
    pausedMarkAttemptedRef.current = false;
  }, []);

  const resetCompletionState = useCallback(() => {
    clearPendingComplete();
    setAwaitingCompletionTransition(false);
    deferredOnFinalizedRef.current = false;
  }, [clearPendingComplete]);

  useEffect(() => {
    resetCompletionState();
  }, [scribeId, resetCompletionState]);

  const notifyScribeRefetch = useCallback(() => {
    dispatch(ambientScribeApi.util.invalidateTags(["AmbientScribes"]));
    eventBus.emit(AMBIENT_SCRIBE_REFETCH, undefined);
  }, [dispatch]);

  const runDeferredOnFinalized = useCallback(() => {
    if (!deferredOnFinalizedRef.current) return;
    deferredOnFinalizedRef.current = false;
    onFinalizedRef.current?.();
  }, []);

  const finishAwaitingCompletion = useCallback(
    (options?: { runDeferredFinalize?: boolean; refetch?: boolean }) => {
      const { runDeferredFinalize = true, refetch = true } = options ?? {};
      setAwaitingCompletionTransition(false);
      if (refetch) {
        notifyScribeRefetch();
      }
      if (runDeferredFinalize) {
        runDeferredOnFinalized();
      } else {
        deferredOnFinalizedRef.current = false;
      }
    },
    [notifyScribeRefetch, runDeferredOnFinalized]
  );

  const doComplete = useCallback(async () => {
    if (!scribeId) return;
    setAwaitingCompletionTransition(true);
    deferredOnFinalizedRef.current = false;

    try {
      const result = await completeScribe({ scribe_id: scribeId }).unwrap();
      notifyScribeRefetch();
      if (result?.cancelled) {
        dispatch(
          setCustomMessage(
            "No transcript found — the scribe has been cancelled."
          )
        );
        dispatch(setOpen(true));
        finishAwaitingCompletion({ runDeferredFinalize: false });
        onFinalizedRef.current?.();
        return;
      }
      dispatch(setCustomMessage("Scribe completion in progress"));
      dispatch(setOpen(true));
      if (onFinalizedRef.current) {
        deferredOnFinalizedRef.current = true;
      }
    } catch (error: any) {
      if (error?.data?.error?.includes("already being processed")) {
        notifyScribeRefetch();
        dispatch(setCustomMessage("Scribe completion in progress"));
        dispatch(setOpen(true));
        if (onFinalizedRef.current) {
          deferredOnFinalizedRef.current = true;
        }
        return;
      }
      finishAwaitingCompletion({ runDeferredFinalize: false });
      dispatch(
        setCustomMessage(
          error?.data?.error ?? "Failed to complete scribe. Please try again."
        )
      );
      dispatch(setOpen(true));
    }
  }, [
    scribeId,
    completeScribe,
    dispatch,
    notifyScribeRefetch,
    finishAwaitingCompletion
  ]);

  useEffect(() => {
    if (!awaitingCompletionTransition) return;
    if (!shouldClearAwaitingCompletion(freshStatus, false)) return;
    finishAwaitingCompletion();
  }, [awaitingCompletionTransition, freshStatus, finishAwaitingCompletion]);

  useEffect(() => {
    if (!awaitingCompletionTransition) return;

    const timer = globalThis.setTimeout(() => {
      dispatch(
        setCustomMessage(
          "Completion is taking longer than expected — refresh or try again."
        )
      );
      dispatch(setOpen(true));
      finishAwaitingCompletion();
    }, AWAITING_COMPLETION_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timer);
  }, [
    awaitingCompletionTransition,
    scribeId,
    dispatch,
    finishAwaitingCompletion
  ]);

  const attemptMarkPaused = useCallback(async () => {
    if (!scribeId || pausedMarkAttemptedRef.current) return;
    pausedMarkAttemptedRef.current = true;
    try {
      await pauseScribe({ scribe_id: scribeId }).unwrap();
      notifyScribeRefetch();
    } catch (error: any) {
      clearPendingComplete();
      const message =
        error?.data?.error ??
        "Could not save the recording for completion. Use Postpone on the recording widget, then try Complete again.";
      dispatch(setCustomMessage(message));
      dispatch(setOpen(true));
      notifyScribeRefetch();
    }
  }, [
    scribeId,
    pauseScribe,
    notifyScribeRefetch,
    clearPendingComplete,
    dispatch
  ]);

  // stop → pause lifecycle (if needed) → /complete/
  useEffect(() => {
    if (!pendingComplete || !scribeId) return;

    if (recordingState === RECORDING_STATE_SAVING) return;

    if (freshStatus === AMBIENT_SCRIBE_STATUS_PAUSED) {
      clearPendingComplete();
      void doComplete();
      return;
    }

    if (isWidgetActive && recordingState === RECORDING_STATE_PAUSED) {
      void attemptMarkPaused();
      return;
    }

    if (isWidgetActive && recordingState !== RECORDING_STATE_STOPPED) {
      return;
    }

    if (!pausedMarkAttemptedRef.current) {
      void attemptMarkPaused();
    }
  }, [
    pendingComplete,
    scribeId,
    recordingState,
    freshStatus,
    isWidgetActive,
    doComplete,
    attemptMarkPaused,
    clearPendingComplete
  ]);

  const handleResume = useCallback(async () => {
    if (resumeActionInFlightRef.current) {
      return;
    }

    if (!patient?.id || !scribeId) {
      dispatch(
        setCustomMessage(
          "Cannot resume — missing patient or scribe information."
        )
      );
      dispatch(setOpen(true));
      return;
    }

    resumeActionInFlightRef.current = true;
    setIsResuming(true);

    try {
      await Promise.race([
        onBeforeAction?.() ?? Promise.resolve(),
        new Promise<void>((resolve) => {
          globalThis.setTimeout(resolve, CONTEXT_SAVE_FLUSH_TIMEOUT_MS);
        })
      ]);

      if (scribeStatus === AMBIENT_SCRIBE_STATUS_COMPLETED) {
        try {
          await editScribe({ scribe_id: scribeId }).unwrap();
        } catch (error: any) {
          const message =
            error?.data?.error ?? "Failed to start editing. Please try again.";
          dispatch(setCustomMessage(message));
          dispatch(setOpen(true));
          return;
        }
      }

      const liveRecordingState = selectMedicalScribeRecordingState(
        store.getState()
      );
      const liveWidgetScribeId = selectStreamingScribeId(store.getState());

      if (
        liveWidgetScribeId != null &&
        liveWidgetScribeId !== Number(scribeId) &&
        liveRecordingState !== RECORDING_STATE_STOPPED
      ) {
        dispatch(
          setCustomMessage(
            "Stop the current recording before resuming this scribe."
          )
        );
        dispatch(setOpen(true));
        return;
      }

      await prepareForResumeFromEditScribe(scribeId);

      const patientFullName = getFullName({
        firstName: patient.first_name ?? "",
        lastName: patient.last_name ?? ""
      });
      const result = await resumePausedScribe({
        scribeId,
        patientId: patient.id,
        patientFullName,
        chartNoteId: chartNoteId ?? undefined
      });

      if (!result.ok) {
        dispatch(
          setCustomMessage(getResumeFailureMessage(result.reason))
        );
        dispatch(setOpen(true));
        return;
      }

      onClose?.();
    } finally {
      resumeActionInFlightRef.current = false;
      setIsResuming(false);
    }
  }, [
    patient,
    scribeId,
    scribeStatus,
    chartNoteId,
    onBeforeAction,
    editScribe,
    resumePausedScribe,
    prepareForResumeFromEditScribe,
    onClose,
    dispatch
  ]);

  const handleComplete = useCallback(async () => {
    if (!scribeId) return;
    if (isCompleting) return;
    await onBeforeAction?.();

    if (canCompleteFromFailedOrCancelled) {
      setIsTerminalCompleteInFlight(true);
      try {
        await editScribe({ scribe_id: scribeId }).unwrap();
        notifyScribeRefetch();
      } catch (error: any) {
        dispatch(
          setCustomMessage(
            error?.data?.error ??
              "Failed to prepare scribe for completion. Please try again."
          )
        );
        dispatch(setOpen(true));
        return;
      } finally {
        setIsTerminalCompleteInFlight(false);
      }
      await doComplete();
      return;
    }

    if (isProcessingStatus) {
      await doComplete();
      return;
    }

    if (isRecordingStatus && !isWidgetActive) {
      await doComplete();
      return;
    }

    if (isScribePaused && recordingState === RECORDING_STATE_STOPPED) {
      await doComplete();
      return;
    }

    if (isWidgetActive && recordingState === RECORDING_STATE_PAUSED) {
      setPendingComplete(true);
      return;
    }

    if (isWidgetActive && recordingState !== RECORDING_STATE_STOPPED) {
      setPendingComplete(true);
      stopRecording();
      return;
    }

    if (isScribePaused) {
      await doComplete();
    }
  }, [
    scribeId,
    isCompleting,
    canCompleteFromFailedOrCancelled,
    editScribe,
    notifyScribeRefetch,
    dispatch,
    isProcessingStatus,
    isRecordingStatus,
    isWidgetActive,
    isScribePaused,
    recordingState,
    onBeforeAction,
    stopRecording,
    doComplete
  ]);

  return {
    handleResume,
    handleComplete,
    isResuming,
    isResumePleaseWait,
    isCompleting,
    isWidgetActive,
    isLocallyPausedWidget,
    isRecordingStatus,
    canCompleteFromFailedOrCancelled,
    isFailedOrCancelledAwaitingChunks
  };
}
